"use client";

import { Link } from "@/lib/i18n/navigation";
import {
  CalendarCheck,
  CalendarBlank,
  CaretRight,
  VideoCamera,
  MapPin,
  Clock,
} from "@phosphor-icons/react/dist/ssr";
import { useUpcomingAppointments } from "./useUpcomingAppointments";
import type { AppointmentsLabels } from "./DesktopUpcomingAppointments";

export type UpcomingAppointment = {
  id: string;
  when: string;
  time: string;
  doctor: string;
  specialty: string;
  location: string;
  type: "video" | "cabinet";
  initials: string;
};

/**
 * Widget "mes prochains RDV" — version mobile de la homepage.
 * SSR affiche toujours l'état "non connecté" (safe à cacher côté CDN) ; ce
 * composant fetch /api/mon-compte/upcoming-appointments après montage —
 * jamais de RDV réel dans le HTML server-rendu (incident 2026-07-20).
 */
export function MobileMyAppointments({ labels }: { labels: AppointmentsLabels }) {
  const { loading, session, appointments } = useUpcomingAppointments();
  const hasAppts = session && appointments.length > 0;

  return (
    <section aria-label={labels.title} className="md:hidden bg-[var(--sahha-surface-1)] pt-5">
      <div className="mx-auto max-w-2xl px-5">
        <div className="mb-3 flex items-end justify-between">
          <h2 className="text-[18px] font-extrabold leading-tight text-[var(--sahha-text-900)]">{labels.title}</h2>
          {hasAppts && (
            <Link href="/mon-compte/rendez-vous" className="text-[12.5px] font-semibold text-[var(--sahha-primary-700)]">
              {labels.viewAll}
            </Link>
          )}
        </div>

        {!session || loading ? (
          <Link
            href="/connexion"
            className="flex items-center gap-3 rounded-[18px] bg-white px-4 py-4 ring-1 ring-[var(--sahha-border-subtle)] transition active:scale-[0.99]"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-100)] text-[var(--sahha-primary-700)]">
              <CalendarCheck weight="duotone" size={22} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-bold leading-tight text-[var(--sahha-text-900)]">{labels.trackTitle}</span>
              <span className="mt-0.5 block text-[12px] text-[var(--sahha-text-500)]">{labels.trackSub}</span>
            </span>
            <CaretRight weight="bold" size={16} className="shrink-0 text-[var(--sahha-text-400)]" />
          </Link>
        ) : !hasAppts ? (
          <Link
            href="/mon-compte/rendez-vous"
            className="flex items-center gap-3 rounded-[18px] bg-white px-4 py-4 ring-1 ring-[var(--sahha-border-subtle)] transition active:scale-[0.99]"
          >
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-100)] text-[var(--sahha-primary-700)]">
              <CalendarBlank weight="duotone" size={22} />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-[14px] font-bold leading-tight text-[var(--sahha-text-900)]">{labels.emptyTitle}</span>
              <span className="mt-0.5 block text-[12px] text-[var(--sahha-text-500)]">{labels.historySub}</span>
            </span>
            <CaretRight weight="bold" size={16} className="shrink-0 text-[var(--sahha-text-400)]" />
          </Link>
        ) : (
          <ul className="space-y-2">
            {appointments.slice(0, 3).map((a) => (
              <li key={a.id}>
                <Link
                  href="/mon-compte/rendez-vous"
                  className="flex items-center gap-3 rounded-[18px] bg-white p-3.5 ring-1 ring-[var(--sahha-border-subtle)] transition active:scale-[0.99]"
                >
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[var(--sahha-primary-100)] text-[14px] font-extrabold text-[var(--sahha-primary-800)]">
                    {a.initials}
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[14px] font-extrabold leading-tight text-[var(--sahha-text-900)]">{a.doctor}</p>
                    <p className="mt-0.5 truncate text-[12px] text-[var(--sahha-text-600)]">{a.specialty}</p>
                    <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11.5px] font-semibold text-[var(--sahha-text-500)]">
                      <span className="inline-flex items-center gap-1 text-[var(--sahha-primary-700)]">
                        <Clock weight="fill" size={11} />
                        {a.when} · {a.time}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        {a.type === "video" ? <VideoCamera weight="fill" size={11} /> : <MapPin weight="fill" size={11} />}
                        {a.location}
                      </span>
                    </div>
                  </div>
                  <CaretRight weight="bold" size={16} className="shrink-0 text-[var(--sahha-text-400)]" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
